/**
 * Flat Mapbox overlay shown when a region/state is "lifted" out of the globe.
 *
 * Extracted from the classic-script scope of the globe monolith (Phase 2,
 * PR-A). Behaviour is unchanged; the formerly free functions and their shared
 * `_mapboxMap` state are now encapsulated in a factory. `mapboxgl` is loaded
 * globally via a <script> tag in the page shell (see globals.d.ts).
 */
import { EU_MEMBER_CODES_IN_DATASET } from '../../../data/eu'
import { colors } from '../../../views/colors'
import { usStatePaths } from '../../../views/us-state-paths'
import { getDataType } from '../data-type'

export interface RegionDatum {
  code: string
  name: string
}

export interface MapEmbed {
  html: string
  mapbox: true
  name: string
  code: string
  zoom: number
  isState?: boolean
}

export interface MapboxOverlayOptions {
  accessToken: string
  regionData: RegionDatum[]
}

export interface MapboxOverlay {
  initMap: (
    containerEl: HTMLElement,
    name: string,
    code: string,
    zoom: number,
    isState?: boolean
  ) => void
  destroy: () => void
  getStateMapEmbed: (stateCode: string) => MapEmbed | null
  getStateName: (stateCode: string) => string
  getRegionMapEmbed: (regionCode: string) => MapEmbed
  getRegionName: (regionCode: string) => string
}

const LOADING_HTML =
  '<div class="map-loading">' +
  '<div class="loading-content">' +
  '<div class="loading-spinner"></div>' +
  'Loading map' +
  '</div>' +
  '</div>'

export function createMapboxOverlay(options: MapboxOverlayOptions): MapboxOverlay {
  const { accessToken, regionData } = options
  const mapId = 'mapbox-container'
  let map: any = null

  function updateHighlight(code: string, isState?: boolean): void {
    if (!map) return
    try {
      if (isState) {
        map.setFilter('state-highlight-fill', ['==', 'name', code])
        map.setFilter('state-highlight-line', ['==', 'name', code])
        map.setFilter('country-highlight-fill', ['==', 'iso_3166_1', ''])
        map.setFilter('country-highlight-line', ['==', 'iso_3166_1', ''])
      } else if (code === 'EU') {
        const euFilter = ['in', 'iso_3166_1'].concat(EU_MEMBER_CODES_IN_DATASET)
        map.setFilter('country-highlight-fill', euFilter)
        map.setFilter('country-highlight-line', euFilter)
        map.setFilter('state-highlight-fill', ['==', 'name', ''])
        map.setFilter('state-highlight-line', ['==', 'name', ''])
      } else {
        map.setFilter('country-highlight-fill', ['==', 'iso_3166_1', code])
        map.setFilter('country-highlight-line', ['==', 'iso_3166_1', code])
        map.setFilter('state-highlight-fill', ['==', 'name', ''])
        map.setFilter('state-highlight-line', ['==', 'name', ''])
      }
    } catch {
      /* layers may not exist yet */
    }
  }

  function initMap(
    containerEl: HTMLElement,
    name: string,
    code: string,
    zoom: number,
    isState?: boolean
  ): void {
    const loadingOverlay =
      containerEl.parentElement && containerEl.parentElement.querySelector('.map-loading')
    const isEU = code === 'EU'

    // If we already have a map instance, just fly to the new location
    if (map && document.getElementById(mapId)) {
      map.resize()
      updateHighlight(code, isState)
      if (isEU) {
        map.flyTo({ center: [10, 50], zoom: zoom, duration: 1000 })
        if (loadingOverlay) loadingOverlay.classList.add('hidden')
        return
      }
      fetch(
        'https://api.mapbox.com/search/geocode/v6/forward?q=' +
          encodeURIComponent(name) +
          '&types=country,region&limit=1&access_token=' +
          accessToken
      )
        .then((r) => r.json())
        .then((data) => {
          if (data.features && data.features.length > 0) {
            const coords = data.features[0].geometry.coordinates
            map.flyTo({ center: coords, zoom: zoom, duration: 1000 })
          }
          if (loadingOverlay) loadingOverlay.classList.add('hidden')
        })
        .catch(() => {
          if (loadingOverlay) loadingOverlay.classList.add('hidden')
        })
      return
    }

    mapboxgl.accessToken = accessToken

    // For EU, skip geocoding and use fixed center
    const euCenterPromise = isEU
      ? Promise.resolve({ center: [10, 50] })
      : fetch(
          'https://api.mapbox.com/search/geocode/v6/forward?q=' +
            encodeURIComponent(name) +
            '&types=country,region&limit=1&access_token=' +
            accessToken
        )
          .then((r) => r.json())
          .then((data) => {
            let c = [0, 20]
            if (data.features && data.features.length > 0) c = data.features[0].geometry.coordinates
            return { center: c }
          })

    euCenterPromise
      .then((result) => {
        const center = result.center

        map = new mapboxgl.Map({
          container: mapId,
          style: 'mapbox://styles/mapbox/light-v11',
          center: center,
          zoom: zoom,
          projection: 'mercator',
          attributionControl: false,
          interactive: false,
        })

        map.on('load', () => {
          // Add country boundary source + layers
          map.addSource('country-boundaries', {
            type: 'vector',
            url: 'mapbox://mapbox.country-boundaries-v1',
          })
          map.addLayer({
            id: 'country-highlight-fill',
            type: 'fill',
            source: 'country-boundaries',
            'source-layer': 'country_boundaries',
            filter: ['==', 'iso_3166_1', ''],
            paint: {
              'fill-color': colors.chartsPrimary,
              'fill-opacity': 0.25,
            },
          })
          map.addLayer({
            id: 'country-highlight-line',
            type: 'line',
            source: 'country-boundaries',
            'source-layer': 'country_boundaries',
            filter: ['==', 'iso_3166_1', ''],
            paint: {
              'line-color': colors.chartsPrimary,
              'line-width': 2,
            },
          })

          // Add US state boundaries from public GeoJSON
          map.addSource('us-states', {
            type: 'geojson',
            data: 'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json',
          })
          map.addLayer({
            id: 'state-highlight-fill',
            type: 'fill',
            source: 'us-states',
            filter: ['==', 'name', ''],
            paint: {
              'fill-color': colors.chartsPrimary,
              'fill-opacity': 0.25,
            },
          })
          map.addLayer({
            id: 'state-highlight-line',
            type: 'line',
            source: 'us-states',
            filter: ['==', 'name', ''],
            paint: {
              'line-color': colors.chartsPrimary,
              'line-width': 2,
            },
          })

          // Apply the initial highlight
          updateHighlight(code, isState)

          if (loadingOverlay) loadingOverlay.classList.add('hidden')
        })
      })
      .catch(() => {
        if (loadingOverlay) loadingOverlay.classList.add('hidden')
      })
  }

  function destroy(): void {
    if (map) {
      map.remove()
      map = null
    }
  }

  function getStateMapEmbed(stateCode: string): MapEmbed | null {
    const stateData = usStatePaths[stateCode.toUpperCase()]
    if (!stateData) return null

    const stateName = stateData.name + ', United States'

    return {
      html: LOADING_HTML + '<div id="' + mapId + '" class="state-map-embed"></div>',
      mapbox: true,
      name: stateName,
      code: stateData.name,
      zoom: 4,
      isState: true,
    }
  }

  function getStateName(stateCode: string): string {
    const stateData = usStatePaths[stateCode.toUpperCase()]
    return stateData ? stateData.name : stateCode
  }

  // Uses Digital IDs data when in Digital IDs view, else Age Assurance regionData
  function getRegionName(regionCode: string): string {
    const dataType = getDataType()
    if (dataType === 'digital_ids') {
      const digitalData = (window.__DIGITAL_IDS_TABLE_DATA__ || []) as RegionDatum[]
      const digitalRegion = digitalData.find(
        (r) => r.code.toUpperCase() === regionCode.toUpperCase()
      )
      if (digitalRegion && digitalRegion.name) return digitalRegion.name
    }
    const region = regionData.find((r) => r.code.toUpperCase() === regionCode.toUpperCase())
    return region ? region.name : regionCode
  }

  function getRegionMapEmbed(regionCode: string): MapEmbed {
    const regionName = getRegionName(regionCode)
    const mapZoom = regionCode.toUpperCase() === 'EU' ? 3 : 2
    return {
      html: LOADING_HTML + '<div id="' + mapId + '" class="state-map-embed"></div>',
      mapbox: true,
      name: regionName,
      code: regionCode,
      zoom: mapZoom,
    }
  }

  return {
    initMap,
    destroy,
    getStateMapEmbed,
    getStateName,
    getRegionMapEmbed,
    getRegionName,
  }
}
